import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useSittingStore from '../../../stores/sittingStore';
import useVisitStore from '../../../stores/visitStore';
import useSittingActions from '../../../hooks/useSittingActions';
import { formatToken } from '../../../utils/formatToken';
import { getTodayIST } from '../../../utils/dateIST';
import RoleTaskBadge from './RoleTaskBadge';
import ProcedureChipList from './ProcedureChipList';

/**
 * NurseTaskWidget — "my assigned sittings" pill on the right edge of
 * the nurse's / junior doctor's screen.
 *
 * Badge: count of sittings the assignee can act on **right now**
 * (Phase 4.0d "My tasks" axis). Hover/click → chip list as on the
 * doctor's per-visit disc.
 *
 * Visibility rules (isActionable predicate):
 *   - awaiting_payment              hidden  (reception's job)
 *   - payment_cleared / scheduled   visible if today/past
 *   - in_progress                   visible
 *   - pending_approval              hidden  (senior's queue)
 *   - needs_reassignment            hidden  (senior's queue)
 *   - completed / paid / cancelled  hidden by default — appear behind
 *                                   the "Show completed" toggle
 *
 * Sort: ascending queue_number (then today-first, then later). Strict-
 * locked chips already sink to the bottom inside ProcedureChipList.
 *
 * Sticky: a chip drops the moment the assignee submits Start /
 * Complete / Reject. If the sitting routes to pending_approval, that's
 * the senior's "Delegated by me" axis — not visible here.
 */

const TERMINAL_STATUSES = new Set(['completed', 'paid', 'cancelled']);
const ACTIONABLE_STATUSES = new Set([
  'payment_cleared', 'scheduled', 'in_progress',
]);

const SHOW_COMPLETED_KEY = (memberId) => `task-widget:show-completed:${memberId || 'anon'}`;

function readShowCompleted(memberId) {
  if (typeof window === 'undefined') return false;
  try { return window.sessionStorage?.getItem(SHOW_COMPLETED_KEY(memberId)) === '1'; }
  catch (_e) { return false; }
}

function writeShowCompleted(memberId, value) {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage?.setItem(SHOW_COMPLETED_KEY(memberId), value ? '1' : '0'); }
  catch (_e) { /* ignore */ }
}

function isToday(scheduledFor) {
  if (!scheduledFor) return true;
  try {
    const datePart = String(scheduledFor).split('T')[0];
    const today = new Date().toISOString().slice(0, 10);
    return datePart <= today;
  } catch { return true; }
}

function isActionable(sitting) {
  const status = sitting?.status;
  if (!ACTIONABLE_STATUSES.has(status)) return false;
  // Future-dated payment_cleared / scheduled sittings live in the
  // calendar, not the action queue. The server already rejects an
  // execute attempt; we hide them upstream so the assignee's pill
  // count reflects what they can actually do.
  if (status !== 'in_progress' && !isToday(sitting?.scheduled_for)) {
    return false;
  }
  return true;
}

function todayYmd() {
  return getTodayIST();
}
function chipDateYmd(s) {
  const ref = s?.scheduled_for || s?.created_at || null;
  if (!ref) return null;
  return String(ref).slice(0, 10);
}
function isPastChip(s, today = todayYmd()) {
  const d = chipDateYmd(s);
  return d != null && d < today;
}

function compareQueueOrder(a, b) {
  const qa = a.queue_number ?? Number.MAX_SAFE_INTEGER;
  const qb = b.queue_number ?? Number.MAX_SAFE_INTEGER;
  if (qa !== qb) return qa - qb;
  // Tie-break: today first (no scheduled_for or today's date), then
  // chronological scheduled_for. Strict-locked chips will still sink
  // to the bottom inside ProcedureChipList — no double-sort needed.
  const sa = a.scheduled_for || '';
  const sb = b.scheduled_for || '';
  return sa.localeCompare(sb);
}

export default function NurseTaskWidget({ viewer, verticalAnchorPx }) {
  const myQueue = useSittingStore((s) => s.myQueue);
  const fetchMyQueue = useSittingStore((s) => s.fetchMyQueue);
  const dailyVisits = useVisitStore((s) => s.dailyVisits);
  const visits = useVisitStore((s) => s.visits);

  useEffect(() => {
    if (typeof fetchMyQueue === 'function') fetchMyQueue().catch(() => {});
  }, [fetchMyQueue]);

  const reload = () => {
    if (typeof fetchMyQueue === 'function') fetchMyQueue().catch(() => {});
  };

  const { handleAction: rawHandleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
  });

  const [showCompleted, setShowCompletedRaw] = useState(
    () => readShowCompleted(viewer?.memberId),
  );
  const setShowCompleted = (next) => {
    setShowCompletedRaw(next);
    writeShowCompleted(viewer?.memberId, next);
    // eslint-disable-next-line no-console
    console.info(`metric=show_completed_toggled axis=mytasks value=${next ? 'on' : 'off'}`);
  };

  // Queue-filter pills — keyed by visit_id (queue numbers reset daily,
  // so visit_id is the only stable identity that won't pull yesterday's
  // chips into today's filter). Past chips fold into a single Past
  // pill rather than generating per-visit pills, so today's view
  // doesn't double up on duplicate Q-X labels.
  const { activeQueues, hasPastPending } = useMemo(() => {
    const visitsMerged = [
      ...(Array.isArray(dailyVisits) ? dailyVisits : []),
      ...(Array.isArray(visits) ? visits : []),
    ];
    const visitInfoById = new Map();
    for (const v of visitsMerged) {
      const vid = v?.visit_id || v?.id;
      if (!vid || visitInfoById.has(vid)) continue;
      visitInfoById.set(vid, {
        queueNumber: v?.queue_number ?? null,
        appointmentNumber: v?.appointment_number ?? null,
      });
    }
    const today = todayYmd();
    const seen = new Set();
    const out = [];
    let pastSeen = false;
    for (const s of myQueue || []) {
      if (TERMINAL_STATUSES.has(s?.status)) continue;
      if (isPastChip(s, today)) {
        pastSeen = true;
        continue;
      }
      if (!isActionable(s)) continue;
      const vid = s?.visit_id;
      if (!vid || seen.has(vid)) continue;
      const meta = visitInfoById.get(vid) || {};
      const qn = s?.queue_number ?? meta.queueNumber;
      const appt = s?.appointment_number ?? meta.appointmentNumber ?? null;
      if (qn == null) continue;
      const raw = formatToken(qn, appt, { hash: false });
      if (!raw) continue;
      seen.add(vid);
      out.push({ visitId: vid, queueNumber: qn, token: `Q-${raw}` });
    }
    out.sort((a, b) => a.queueNumber - b.queueNumber);
    return { activeQueues: out, hasPastPending: pastSeen };
  }, [dailyVisits, visits, myQueue]);
  const activeQueueKeySig = `${activeQueues.map((q) => q.visitId).join('|')}|past:${hasPastPending ? '1' : '0'}`;

  const [queueFilter, setQueueFilter] = useState(null);
  useEffect(() => {
    if (queueFilter == null) return;
    if (queueFilter === 'past') {
      if (!hasPastPending) setQueueFilter(null);
      return;
    }
    if (!activeQueues.some((q) => q.visitId === queueFilter)) {
      setQueueFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQueueKeySig]);
  const queueFilterActive = queueFilter != null;

  // When a queue filter is active, the patient is already
  // disambiguated, so skip the start-confirmation modal.
  const handleAction = useCallback(
    (event) => rawHandleAction({ ...event, skipConfirm: queueFilterActive }),
    [rawHandleAction, queueFilterActive],
  );

  const matchesFilter = (s) => {
    if (queueFilter == null) return true;
    if (queueFilter === 'past') return isPastChip(s);
    if (isPastChip(s)) return false;
    return s?.visit_id === queueFilter;
  };

  const { actionable, completed } = useMemo(() => {
    const a = [];
    const c = [];
    const inPastView = queueFilter === 'past';
    for (const s of myQueue || []) {
      if (!matchesFilter(s)) continue;
      if (TERMINAL_STATUSES.has(s.status)) c.push(s);
      else if (inPastView) a.push(s); // past view: every non-terminal
                                      // chip is shown, including ones
                                      // that wouldn't pass isActionable
                                      // (yesterday's payment_cleared).
      else if (isActionable(s)) a.push(s);
      // Anything else (awaiting_payment, pending_approval,
      // needs_reassignment, future-dated) is intentionally dropped.
    }
    a.sort(compareQueueOrder);
    c.sort(compareQueueOrder);
    return { actionable: a, completed: c };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myQueue, queueFilter]);

  const completedCount = completed.length;
  // The closed badge count is unfiltered and counts every non-
  // terminal chip the nurse owns — including past-dated chips that
  // wouldn't pass `isActionable` — so the dock surfaces the moment
  // there's any pending work, not just today's actionable subset.
  const unfilteredPendingCount = useMemo(() => {
    let n = 0;
    for (const s of myQueue || []) {
      if (TERMINAL_STATUSES.has(s?.status)) continue;
      if (s?.status === 'awaiting_payment') continue; // reception's lane
      n += 1;
    }
    return n;
  }, [myQueue]);
  const count = unfilteredPendingCount;
  // When a queue filter is active, force-include completed chips so
  // the user sees the full done + pending picture for that visit.
  const visibleSittings = (showCompleted || queueFilterActive)
    ? [...actionable, ...completed]
    : actionable;

  return (
    <>
      <RoleTaskBadge
        count={count}
        countLabel={count === 1 ? 'TASK' : 'TASKS'}
        title="My tasks"
        tone="teal"
        verticalAnchorPx={verticalAnchorPx}
        ariaLabel={`${count} task${count === 1 ? '' : 's'} ready for you`}
        metricKey="mytasks"
      >
        {({ palette }) => (
          <>
            {(activeQueues.length > 0 || hasPastPending) && (
              <div
                role="tablist"
                aria-label="Filter by patient queue"
                style={{
                  display: 'flex',
                  gap: 4,
                  padding: 3,
                  borderRadius: 8,
                  background: palette.bgAlt,
                  border: `1px solid ${palette.border}`,
                  marginBottom: 6,
                  flexWrap: 'wrap',
                }}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={queueFilter == null}
                  onClick={() => setQueueFilter(null)}
                  className={queueFilter == null
                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                    : 'bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'}
                  style={{
                    flex: 1,
                    minWidth: 70,
                    padding: '0.3rem 0.5rem',
                    borderRadius: 6,
                    border: queueFilter == null ? 'none' : `1px solid ${palette.border}`,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Overall
                </button>
                {activeQueues.map(({ visitId, token }) => {
                  const isActive = queueFilter === visitId;
                  return (
                    <button
                      key={visitId}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setQueueFilter(visitId)}
                      className={isActive
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : 'bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'}
                      style={{
                        flex: 1,
                        minWidth: 56,
                        padding: '0.3rem 0.5rem',
                        borderRadius: 6,
                        border: isActive ? 'none' : `1px solid ${palette.border}`,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {token}
                    </button>
                  );
                })}
                {hasPastPending && (() => {
                  const isActive = queueFilter === 'past';
                  return (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setQueueFilter('past')}
                      className={isActive
                        ? 'bg-slate-600 text-white hover:bg-slate-700'
                        : 'bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'}
                      style={{
                        flex: 1,
                        minWidth: 56,
                        padding: '0.3rem 0.5rem',
                        borderRadius: 6,
                        border: isActive ? 'none' : `1px solid ${palette.border}`,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Past
                    </button>
                  );
                })()}
              </div>
            )}
            {count === 0 && completedCount === 0 && (
              <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
                Nothing assigned to you right now.
              </p>
            )}
            {count === 0 && completedCount > 0 && !showCompleted && !queueFilterActive && (
              <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
                All caught up. {completedCount} completed today — toggle below to view.
              </p>
            )}
            {visibleSittings.length > 0 && (
              <ProcedureChipList
                sittings={visibleSittings}
                viewer={viewer}
                onAction={handleAction}
                onDelete={null}
                compact={false}
                emptyHint={null}
                showPatient
              />
            )}
            {completedCount > 0 && !queueFilterActive && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.6875rem',
                  paddingTop: 4,
                  borderTop: `1px solid ${palette.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
                Show completed ({completedCount}
                {showCompleted ? ' shown' : ' hidden'})
              </label>
            )}
          </>
        )}
      </RoleTaskBadge>
      {dialogs}
    </>
  );
}
